import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { AccApplication, LanguageId } from '@interfaces';
import { DomainDataService } from '../../../services';
import { AdmissionsAreaService } from '../admissions-area.service';

interface StatusCount {
	status: string,
	english: number,
	spanish: number,
	order?: number,
}

@Component({
	selector: 'application-status-counts-widget',
	standalone: true,
	templateUrl: './application-status-counts.widget.html',
	changeDetection: ChangeDetectionStrategy.Eager,
})
export class ApplicationStatusCountsWidget {

	private domainDataService = inject(DomainDataService);
	private areaService = inject(AdmissionsAreaService);

	public statusCount = computed(() => {
		const accApplications = this.areaService.admissionsData()?.accApplications;
		if (!accApplications) return;

		const statusMap = this.domainDataService.data().accApplicationStatusesMap;

		// Initialize counts with all possible statuses
		const initialCounts: Record<number, StatusCount> = Object.entries(statusMap).reduce(
			(acc, [id, status]) => ({
				...acc,
				[id]: { status: status.name, english: 0, spanish: 0, order: status.order }
			}),
			{} as Record<number, StatusCount>
		);

		// Count applications by status and language
		const { counts, total } = accApplications.reduce(
			(result: { counts: Record<number, StatusCount>, total: StatusCount }, app: AccApplication) => {
				const key = app.accApplicationStatusId;
				const language = app.languageId === LanguageId.English ? 'english' : 'spanish';

				// Update status count and total
				if (result.counts[key]) {
					result.counts[key][language]++;
				}
				result.total[language]++;

				return result;
			},
			{
				counts: initialCounts,
				total: { status: 'Total', english: 0, spanish: 0 }
			}
		);

		// Convert to array, sort by order, and add total
		const sortedCounts = Object.values(counts)
			.sort((a, b) => (a.order || 0) - (b.order || 0));
		return [...sortedCounts, total];

	})
}
